import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Card,
  CardContent,
  Chip,
  Divider,
  Fade,
  Stack,
  Tooltip,
  useTheme,
  alpha,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses,
  Autocomplete,
  AutocompleteRenderInputParams,
  List,
  ListItem,
  ListItemText,
  Container,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  VideoLibrary as VideoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Timeline as TimelineIcon,
  DataUsage as DataUsageIcon,
  DataSaverOff as DataOffIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import RegistrosService, {
  Registro,
  RegistroCreate,
  RegistroUpdate,
  RegistroFilter,
} from '../../services/registrosService';

import EmissorasService from '../../services/emissorasService';
import MarcasService, { MarcaCreate } from '../../services/marcasService';
import ItensService, { Item as ItemType, ItemCreate } from '../../services/itensService';
import SetoresService from '../../services/setoresService';
import ProgramasService, { ProgramaCreate } from '../../services/programasService';
import AgenciasService, { AgenciaCreate } from '../../services/agenciasService';
import AnunciantesService, { AnuncianteCreate } from '../../services/anunciantesService';
import CategoriasService, { CategoriaCreate } from '../../services/categoriasService';

// ————————————————— Busca dos campos de seleção —————————————————
// Normaliza para comparação: minúsculas, sem acento, espaços colapsados.
// Assim "SÃO  PAULO" e "sao paulo" são o mesmo texto para a busca.
const normalizarTexto = (valor: unknown): string =>
  String(valor ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

// Filtra a lista pelo que foi digitado. Todas as palavras do termo precisam
// aparecer no nome, em qualquer ordem — "no ar sc" também acha "SC NO AR".
// Ordem do resultado: nome exato → começa com o termo → contém o termo →
// contém todas as palavras soltas; alfabético dentro de cada grupo.
function filtrarPorTermo<T extends { nome?: string }>(
  opcoes: T[],
  termo: string,
  limite = 50
): T[] {
  const alvo = normalizarTexto(termo);
  const alfabetico = [...opcoes].sort((a, b) =>
    normalizarTexto(a.nome).localeCompare(normalizarTexto(b.nome))
  );

  // Sem nada digitado não despejamos o catálogo inteiro na tela (há campos com
  // dezenas de milhares de itens); mostramos só o começo da lista.
  if (!alvo) return alfabetico.slice(0, limite);

  const palavras = alvo.split(' ');
  const pontuar = (nome: string): number => {
    if (nome === alvo) return 0;
    if (nome.startsWith(alvo)) return 1;
    if (nome.includes(alvo)) return 2;
    if (palavras.every((p) => nome.includes(p))) return 3;
    return 99;
  };

  return alfabetico
    .map((opcao) => ({ opcao, peso: pontuar(normalizarTexto(opcao.nome)) }))
    .filter((x) => x.peso < 99)
    .sort((a, b) => a.peso - b.peso)
    .slice(0, limite)
    .map((x) => x.opcao);
}

const RegistrosPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // Rota /registros/novo renderiza o cadastro como PÁGINA (antes era um modal)
  const isNovoPage = location.pathname === '/registros/novo';
  
  // Define standard height for filter elements
  const FILTER_ELEMENT_HEIGHT = '40px';

  // Último texto digitado em cada campo de busca. O MUI, em algumas situações,
  // entrega string vazia para o filtro (quando considera que o texto é apenas o
  // rótulo do item já selecionado) — e aí a lista aparecia SEM filtro nenhum,
  // como no caso do Programa "SC NO AR". Guardamos o que foi digitado para não
  // depender disso.
  const termosBusca = React.useRef<Record<string, string>>({});
  const registrarBusca = (campo: string) => (_e: any, valor: string) => {
    termosBusca.current[campo] = valor;
  };
  const termoBusca = (
    campo: string,
    state: { inputValue: string },
    labelSelecionado?: string
  ): string => {
    const digitado = state.inputValue !== '' ? state.inputValue : (termosBusca.current[campo] || '');
    // Se o texto é exatamente o nome do item já escolhido, o usuário não está
    // buscando nada: mostramos a lista inteira (comportamento padrão do MUI).
    if (labelSelecionado && normalizarTexto(digitado) === normalizarTexto(labelSelecionado)) {
      return '';
    }
    return digitado;
  };
  // Monta o filtro de um campo: busca inteligente + opção "Criar ..." no fim.
  const filtroDoCampo = (
    campo: string,
    labelSelecionado: () => string | undefined,
    opcoes: { limite?: number; permitirCriar?: boolean } = {}
  ) => (options: any[], state: { inputValue: string }) => {
    const termo = termoBusca(campo, state, labelSelecionado());
    const filtrados = filtrarPorTermo(options, termo, opcoes.limite ?? 50);

    if (
      opcoes.permitirCriar !== false &&
      normalizarTexto(termo) !== '' &&
      !filtrados.some((x) => normalizarTexto(x.nome) === normalizarTexto(termo))
    ) {
      filtrados.push({ id: '-1' as any, nome: `Criar "${termo.trim()}"` });
    }

    return filtrados;
  };
  
  // Custom ListBox component to fix the dropdown position
  const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
      return (
        <div
          ref={ref}
          {...props}
          style={{
            ...props.style,
            maxHeight: '200px',
            position: 'relative',
            zIndex: 9999,
          }}
        />
      );
    }
  );
  
  // Estado de listagem
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de filtros
  const [filter, setFilter] = useState<RegistroFilter>(() => {
    // Set default date range to last 7 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    // Format dates to ISO string (YYYY-MM-DDThh:mm:ss.sssZ)
    const datahora_inicio = startDate.toISOString().split('T')[0] + 'T00:00:00';
    const datahora_fim = endDate.toISOString().split('T')[0] + 'T23:59:59';
    
    return {
      datahora_inicio,
      datahora_fim
    };
  });
  // Removed qualidade and genero filters
  const [emissorasDisponiveis, setEmissorasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [marcasDisponiveis, setMarcasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [itensDisponiveis, setItensDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [setoresDisponiveis, setSetoresDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [programasDisponiveis, setProgramasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [agenciasDisponiveis, setAgenciasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [anunciantesDisponiveis, setAnunciantesDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [categoriasDisponiveis, setCategoriasDisponiveis] = useState<Array<{id: number, nome: string}>>([]);
  const [showFilters, setShowFilters] = useState(false);

  // Estado de formulários
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentRegistro, setCurrentRegistro] = useState<Registro | null>(null);
  const [newRegistro, setNewRegistro] = useState<RegistroCreate>({});
  const [editRegistro, setEditRegistro] = useState<RegistroUpdate>({});
  const [pendingRegistros, setPendingRegistros] = useState<RegistroCreate[]>([]);

  // "Manter informações": com isso marcado, ao "Adicionar à Lista" os campos
  // Marca, Título e Duração (e os vínculos que vêm da marca) continuam
  // preenchidos para o próximo cadastro. Fica lembrado entre sessões.
  const [manterInfo, setManterInfo] = useState<boolean>(
    () => localStorage.getItem('registros_manter_info') === '1'
  );

  // Diálogo de confirmação genérico (limpar formulário, fechar com dados não salvos, excluir salvo)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', confirmLabel: 'Confirmar', onConfirm: () => {} });

  const askConfirm = (title: string, message: string, onConfirm: () => void, confirmLabel = 'Confirmar') => {
    setConfirmDialog({ open: true, title, message, confirmLabel, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  // Sugestões de título já usados (autocomplete) — melhoria pedida pelo cliente
  const [tituloSugestoes, setTituloSugestoes] = useState<string[]>([]);

  // IDs dos itens vinculados à marca selecionada (para priorizar no dropdown de Item)
  const [marcaItemIds, setMarcaItemIds] = useState<Set<number>>(new Set());

  // Data e Hora são dois campos na tela, mas gravam num único `datahora`
  // (formato YYYY-MM-DDTHH:mm) — o backend e o banco não mudam.
  const [dataParte, setDataParte] = useState('');
  const [horaParte, setHoraParte] = useState('');
  const combinarDataHora = (data: string, hora: string) => {
    if (!data || !hora) return '';
    // Aceita HH:mm ou HH:mm:ss — se vier sem segundos, assume :00
    const h = hora.length === 5 ? `${hora}:00` : hora;
    return `${data}T${h}`;
  };
  const handleDataParte = (valor: string) => {
    setDataParte(valor);
    handleChangeNewRegistro('datahora', combinarDataHora(valor, horaParte));
  };
  const handleHoraParte = (valor: string) => {
    setHoraParte(valor);
    handleChangeNewRegistro('datahora', combinarDataHora(dataParte, valor));
  };
  const resetDataHoraPartes = () => { setDataParte(''); setHoraParte(''); };
  const fetchTituloSugestoes = useCallback(async (q: string) => {
    try {
      const data = await RegistrosService.getTitulos(q, 20);
      setTituloSugestoes(data);
    } catch (error) {
      console.error('Erro ao buscar sugestões de título:', error);
    }
  }, []);

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Add states for marca creation
  const [openCreateMarcaDialog, setOpenCreateMarcaDialog] = useState(false);
  const [newMarca, setNewMarca] = useState<MarcaCreate>({ nome: '' });
  const [marcasPrincipais, setMarcasPrincipais] = useState<{id: number, nome: string}[]>([]);
  
  // Add states for item creation
  const [openCreateItemDialog, setOpenCreateItemDialog] = useState(false);
  const [newItem, setNewItem] = useState<ItemCreate>({ nome: '' });
  const [itensPrincipais, setItensPrincipais] = useState<{id: number, nome: string}[]>([]);
  
  // Add states for agencia creation
  const [openCreateAgenciaDialog, setOpenCreateAgenciaDialog] = useState(false);
  const [newAgencia, setNewAgencia] = useState<AgenciaCreate>({ nome: '' });
  const [agenciasPrincipais, setAgenciasPrincipais] = useState<{id: number, nome: string}[]>([]);
  
  // Add states for anunciante creation
  const [openCreateAnuncianteDialog, setOpenCreateAnuncianteDialog] = useState(false);
  const [newAnunciante, setNewAnunciante] = useState<AnuncianteCreate>({ nome: '' });
  const [anunciantesPrincipais, setAnunciantesPrincipais] = useState<{id: number, nome: string}[]>([]);
  
  // Add states for categoria creation
  const [openCreateCategoriaDialog, setOpenCreateCategoriaDialog] = useState(false);
  const [newCategoria, setNewCategoria] = useState<CategoriaCreate>({ nome: '' });
  const [categoriasPrincipais, setCategoriasPrincipais] = useState<{id: number, nome: string}[]>([]);

  // Add states for programa creation
  const [openCreateProgramaDialog, setOpenCreateProgramaDialog] = useState(false);
  const [newPrograma, setNewPrograma] = useState<ProgramaCreate>({ nome: '' });
  
  // Add states for setor creation
  const [openCreateSetorDialog, setOpenCreateSetorDialog] = useState(false);
  const [newSetor, setNewSetor] = useState<{nome: string, principal_id?: number}>({ nome: '' });
  const [setoresPrincipais, setSetoresPrincipais] = useState<{id: number, nome: string}[]>([]);

  // Add a function to fetch marcas principais
  const fetchMarcasPrincipais = useCallback(async () => {
    try {
      const data = await MarcasService.getMarcas({}, 0, 100);
      setMarcasPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar marcas principais:', error);
    }
  }, []);

  // Add a function to fetch agencias principais
  const fetchAgenciasPrincipais = useCallback(async () => {
    try {
      const data = await AgenciasService.getAllAgencias();
      setAgenciasPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar agências principais:', error);
    }
  }, []);

  // Add a function to fetch anunciantes principais
  const fetchAnunciantesPrincipais = useCallback(async () => {
    try {
      const data = await AnunciantesService.getAllAnunciantes();
      setAnunciantesPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar anunciantes principais:', error);
    }
  }, []);

  // Add a function to fetch categorias principais
  const fetchCategoriasPrincipais = useCallback(async () => {
    try {
      const data = await CategoriasService.getCategorias({}, 0, 100);
      setCategoriasPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar categorias principais:', error);
    }
  }, []);

  // Add a function to fetch itens principais
  const fetchItensPrincipais = useCallback(async () => {
    try {
      const data = await ItensService.getAllItens();
      setItensPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar itens principais:', error);
    }
  }, []);

  // Add a function to fetch setores principais
  const fetchSetoresPrincipais = useCallback(async () => {
    try {
      const data = await SetoresService.getSetores({}, 0, 1000);
      setSetoresPrincipais(data);
    } catch (error) {
      console.error('Erro ao buscar setores principais:', error);
    }
  }, []);

  // Carregar dados de filtros
  const fetchFilterData = useCallback(async () => {
    try {
      console.log('Buscando dados de filtros...');
      
      // Buscar dados gerais
      // Removed qualidade and genero promises
      const emissorasPromise = EmissorasService.getEmissoras({}, 0, 1000);
      const itensPromise = ItensService.getAllItens();
      const setoresPromise = SetoresService.getSetores({}, 0, 1000);
      const programasPromise = ProgramasService.getAllProgramas();
      const agenciasPromise = AgenciasService.getAllAgencias();
      const anunciantesPromise = AnunciantesService.getAllAnunciantes();
      const categoriasPromise = CategoriasService.getAllCategorias();
      
      const [emissoras, itens, setores, programas, agencias, anunciantes, categorias] = await Promise.all([
        emissorasPromise,
        itensPromise,
        setoresPromise,
        programasPromise,
        agenciasPromise,
        anunciantesPromise,
        categoriasPromise
      ]);
      
      // Removed logs for qualidade and genero
      console.log('Dados de emissoras recebidos:', emissoras);
      console.log('Dados de itens recebidos:', itens);
      console.log('Dados de setores recebidos:', setores);
      console.log('Dados de programas recebidos:', programas);
      console.log('Dados de agências recebidos:', agencias);
      console.log('Dados de anunciantes recebidos:', anunciantes);
      console.log('Dados de categorias recebidos:', categorias);
      
      // Removed state setting for qualidade and genero
      setEmissorasDisponiveis(Array.isArray(emissoras) ? emissoras : []);
      setItensDisponiveis(Array.isArray(itens) ? itens : []);
      setSetoresDisponiveis(Array.isArray(setores) ? setores : []);
      setProgramasDisponiveis(Array.isArray(programas) ? programas : []);
      setAgenciasDisponiveis(Array.isArray(agencias) ? agencias : []);
      setAgenciasPrincipais(Array.isArray(agencias) ? agencias : []); // Set agenciasPrincipais
      setAnunciantesDisponiveis(Array.isArray(anunciantes) ? anunciantes : []);
      setAnunciantesPrincipais(Array.isArray(anunciantes) ? anunciantes : []); // Set anunciantesPrincipais
      setCategoriasDisponiveis(Array.isArray(categorias) ? categorias : []);
      
      // Buscar marcas em uma chamada separada para garantir execução
      console.log('Buscando marcas...');
      try {
        const marcasData = await MarcasService.getMarcas({}, 0, 50000); // Increased limit to 50000 to fetch all brands
        console.log('Marcas obtidas:', marcasData);
        
        // Verificações adicionais para debug
        if (marcasData && Array.isArray(marcasData)) {
          console.log('Total de marcas encontradas:', marcasData.length);
          setMarcasDisponiveis(marcasData);
          setMarcasPrincipais(marcasData); // Also set marcasPrincipais
        } else {
          console.log('Resposta de marcas não é um array válido, tipo:', typeof marcasData);
          // Tentativa de conversão ou valor padrão
          setMarcasDisponiveis([]);
          setMarcasPrincipais([]); // Also set marcasPrincipais as empty array
        }
      } catch (marcaError) {
        console.error('Erro ao buscar marcas:', marcaError);
        // Em caso de erro, definir um array vazio
        setMarcasDisponiveis([]);
        setMarcasPrincipais([]); // Also set marcasPrincipais as empty array
      }
    } catch (error) {
      console.error('Erro ao carregar dados de filtros:', error);
    }
  }, []);

  const fetchRegistros = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      
      // Log the filter state for debugging
      console.log('[RegistrosPage] Current filter state:', filter);
      
      // Obter registros e contagem total
      const [registros, count] = await Promise.all([
        RegistrosService.getRegistros(filter, skip, limit),
        RegistrosService.getRegistroCount(filter)
      ]);
      
      // Log the received data
      console.log(`[RegistrosPage] Received ${registros.length} registros from API`);
      
      setRegistros(registros);
      setTotalCount(count);
    } catch (error) {
      console.error('Erro ao buscar registros:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar registros',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar dados de filtros ao iniciar
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    // Buscar registros ao iniciar a página ou mudar filtros/paginação
    fetchRegistros();
  }, [fetchRegistros]);

  // Add a useEffect to fetch marcas principais when dialog opens
  useEffect(() => {
    if (openCreateMarcaDialog) {
      fetchMarcasPrincipais();
    }
  }, [openCreateMarcaDialog, fetchMarcasPrincipais]);

  // Add a useEffect to fetch agencias principais when dialog opens
  useEffect(() => {
    if (openCreateAgenciaDialog) {
      fetchAgenciasPrincipais();
    }
  }, [openCreateAgenciaDialog, fetchAgenciasPrincipais]);

  // Add a function to fetch anunciantes principais when dialog opens
  useEffect(() => {
    if (openCreateAnuncianteDialog) {
      fetchAnunciantesPrincipais();
    }
  }, [openCreateAnuncianteDialog, fetchAnunciantesPrincipais]);

  // Effect to check if we need to fetch data for creation dialogs
  useEffect(() => {
    if (openCreateCategoriaDialog && categoriasPrincipais.length === 0) {
      fetchCategoriasPrincipais();
    }
  }, [openCreateCategoriaDialog, categoriasPrincipais.length, fetchCategoriasPrincipais]);

  // Effect to fetch itens principais when the item creation dialog is opened
  useEffect(() => {
    if (openCreateItemDialog) {
      fetchItensPrincipais();
    }
  }, [openCreateItemDialog, fetchItensPrincipais]);

  // Effect to fetch agencias principais when the agencia creation dialog is opened
  useEffect(() => {
    if (openCreateAgenciaDialog) {
      fetchAgenciasPrincipais();
    }
  }, [openCreateAgenciaDialog, fetchAgenciasPrincipais]);

  // Fetch categorias principais when dialog opens
  useEffect(() => {
    if (openCreateCategoriaDialog) {
      fetchCategoriasPrincipais();
    }
  }, [openCreateCategoriaDialog, fetchCategoriasPrincipais]);

  // Fetch setores principais when dialog opens
  useEffect(() => {
    if (openCreateSetorDialog) {
      fetchSetoresPrincipais();
    }
  }, [openCreateSetorDialog, fetchSetoresPrincipais]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof RegistroFilter, value: any) => {
    // For emissora_id specifically, we just pass it directly without converting to emissora_ids
    // since the backend expects emissora_id
    setFilter((prev: RegistroFilter) => ({
      ...prev,
      [field]: value || undefined, // Usar undefined para remover o filtro vazio
    }));
    setPage(0); // Resetar paginação ao mudar filtro
  };

  const handleClearFilters = () => {
    // When clearing filters, reset to the default date range (last 7 days)
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    
    // Format dates to ISO string (YYYY-MM-DDThh:mm:ss.sssZ)
    const datahora_inicio = startDate.toISOString().split('T')[0] + 'T00:00:00';
    const datahora_fim = endDate.toISOString().split('T')[0] + 'T23:59:59';
    
    setFilter({
      datahora_inicio,
      datahora_fim
    });
    setPage(0);
  };

  const handleCreateRegistro = async () => {
    try {
      // Extract the date part for dataabsoluta
      const dataabsoluta = newRegistro.datahora ? newRegistro.datahora.substring(0, 10) : undefined;
      
      // DEBUG: Log the entire newRegistro object
      console.log('=== DEBUG handleCreateRegistro ===');
      console.log('newRegistro completo:', JSON.stringify(newRegistro, null, 2));
      console.log('pi_number específico:', newRegistro.pi_number);
      console.log('Object.keys(newRegistro):', Object.keys(newRegistro));
      
      const dataToSend = {
        ...newRegistro,
        dataabsoluta
      };
      console.log('Dados sendo enviados:', JSON.stringify(dataToSend, null, 2));
      
      // Add dataabsoluta to the new registro
      await RegistrosService.createRegistro(dataToSend);
      
      setOpenCreateDialog(false);
      setSnackbar({
        open: true,
        message: 'Registro criado com sucesso',
        severity: 'success',
      });
      // Limpar formulário
      setNewRegistro({});
      // Recarregar dados
      fetchRegistros();
    } catch (error) {
      console.error('Erro ao criar registro:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar registro',
        severity: 'error',
      });
    }
  };

  const handleOpenEditDialog = (registro: Registro) => {
    console.log('Abrindo diálogo de edição, dados do registro:', registro);
    setCurrentRegistro(registro);
    // Inicializar dados para edição
    setEditRegistro({
      datahora: registro.datahora,
      dataabsoluta: registro.datahora ? registro.datahora.substring(0, 10) : undefined,
      titulo: registro.titulo,
      tipo: registro.tipo,
      duracao: registro.duracao,
      custo: registro.custo,
      qualidade: registro.qualidade,
      genero: registro.genero,
      mercado: registro.mercado,
      emissora_id: registro.emissora_id,
      marca_id: registro.marca_id,
      item_id: registro.item_id,
      programa_id: registro.programa_id,
      anunciante_id: registro.anunciante_id,
      setor_id: registro.setor_id,
      agencia_id: registro.agencia_id,
      precisao: registro.precisao,
      sincronia: registro.sincronia,
      consideracoes: registro.consideracoes,
      pi_number: registro.pi_number
    });
    
    // Garantir que temos dados de anunciantes, marcas e itens antes de abrir o diálogo
    if (!anunciantesDisponiveis || anunciantesDisponiveis.length === 0) {
      console.log('Buscando anunciantes antes de abrir o diálogo de edição...');
      AnunciantesService.getAllAnunciantes().then(data => {
        console.log('Anunciantes recebidos para diálogo de edição:', data);
        setAnunciantesDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar anunciantes para edição:', err));
    }
    
    if (!marcasDisponiveis || marcasDisponiveis.length === 0) {
      console.log('Buscando marcas antes de abrir o diálogo de edição...');
      MarcasService.getMarcas({}, 0, 50000).then(data => { // Increased limit to 50000
        console.log('Marcas recebidas para diálogo de edição:', data);
        setMarcasDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar marcas para edição:', err));
    }
    
    if (!itensDisponiveis || itensDisponiveis.length === 0) {
      console.log('Buscando itens antes de abrir o diálogo de edição...');
      ItensService.getAllItens().then(data => {
        console.log('Itens recebidos para diálogo de edição:', data);
        setItensDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar itens para edição:', err));
    }
    
    if (!setoresDisponiveis || setoresDisponiveis.length === 0) {
      console.log('Buscando setores antes de abrir o diálogo de edição...');
      SetoresService.getSetores({}, 0, 1000).then(data => {
        console.log('Setores recebidos para diálogo de edição:', data);
        setSetoresDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar setores para edição:', err));
    }
    
    if (!programasDisponiveis || programasDisponiveis.length === 0) {
      console.log('Buscando programas antes de abrir o diálogo de edição...');
      ProgramasService.getAllProgramas().then(data => {
        console.log('Programas recebidos para diálogo de edição:', data);
        setProgramasDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar programas para edição:', err));
    }
    
    if (!categoriasDisponiveis || categoriasDisponiveis.length === 0) {
      console.log('Buscando categorias antes de abrir o diálogo de edição...');
      CategoriasService.getAllCategorias().then(data => {
        console.log('Categorias recebidas para diálogo de edição:', data);
        setCategoriasDisponiveis(Array.isArray(data) ? data : []);
      }).catch(err => console.error('Erro ao buscar categorias para edição:', err));
    }
    
    setOpenEditDialog(true);
  };

  const handleEditRegistro = async () => {
    if (!currentRegistro) return;

    try {
      // Update dataabsoluta when datahora changes
      const dataabsoluta = editRegistro.datahora ? editRegistro.datahora.substring(0, 10) : undefined;
      
      await RegistrosService.updateRegistro(currentRegistro.id, {
        ...editRegistro,
        dataabsoluta
      });
      
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Registro atualizado com sucesso',
        severity: 'success',
      });

      // Se o registro editado também está na lista "salvos nesta sessão"
      // (cadastro em andamento), atualiza lá para não mostrar dado velho.
      setPendingRegistros(prev =>
        prev.map((r: any) =>
          r && r.id === currentRegistro.id
            ? { ...r, ...editRegistro, dataabsoluta }
            : r
        )
      );

      // Limpar estados
      setCurrentRegistro(null);
      setEditRegistro({});
      // Recarregar dados
      fetchRegistros();
    } catch (error) {
      console.error('Erro ao atualizar registro:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar registro',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (registro: Registro) => {
    setCurrentRegistro(registro);
    setOpenDeleteDialog(true);
  };

  const handleDeleteRegistro = async () => {
    if (!currentRegistro) return;

    try {
      await RegistrosService.deleteRegistro(currentRegistro.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Registro removido com sucesso',
        severity: 'success',
      });
      // Limpar estado
      setCurrentRegistro(null);
      // Recarregar dados
      fetchRegistros();
    } catch (error) {
      console.error('Erro ao remover registro:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao remover registro',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleChangeNewRegistro = (field: string, value: any) => {
    // DEBUG: Log every field change
    console.log(`=== handleChangeNewRegistro: ${field} = "${value}" ===`);
    
    let updates: any = { [field]: value };
    
    // If datahora changes, update dataabsoluta accordingly
    if (field === 'datahora' && value) {
      updates.dataabsoluta = value.substring(0, 10);
    }
    
    setNewRegistro((prev: RegistroCreate) => {
      const newState = { ...prev, ...updates };
      console.log('Novo estado newRegistro:', JSON.stringify(newState, null, 2));
      return newState;
    });
  };

  const handleChangeEditRegistro = (field: string, value: any) => {
    let updates: any = { [field]: value };
    
    // If datahora changes, update dataabsoluta accordingly
    if (field === 'datahora' && value) {
      updates.dataabsoluta = value.substring(0, 10);
    }
    
    setEditRegistro((prev: RegistroUpdate) => ({ ...prev, ...updates }));
  };

  const formatDateTimeDisplay = (dateString?: string) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR });
    } catch (error) {
      return dateString;
    }
  };

  const isValidDate = (dateString?: string) => {
    if (!dateString) return false;
    try {
      const date = parseISO(dateString);
      return isValid(date);
    } catch (error) {
      return false;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Add a handle function for marca creation
  const handleCreateMarca = async () => {
    try {
      // Só o nome é obrigatório (o banco e o backend não exigem marca principal,
      // e 99,7% das marcas não usam esse vínculo)
      if (!newMarca.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da marca é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await MarcasService.createMarca(newMarca);
      setOpenCreateMarcaDialog(false);
      
      // Add the new marca to the available marcas
      setMarcasDisponiveis(prev => [...prev, result]);
      
      // Set the newly created marca as the selected one
      handleChangeNewRegistro('marca_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Marca criada com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewMarca({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar marca:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar marca',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the marca creation dialog
  const handleMarcaSelectChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const name = event.target.name as string;
    const value = event.target.value as number;
    
    setNewMarca(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Add a handle function for agencia creation
  const handleCreateAgencia = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newAgencia.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da agência é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await AgenciasService.createAgencia(newAgencia);
      setOpenCreateAgenciaDialog(false);
      
      // Add the new agencia to the available agencias
      setAgenciasDisponiveis(prev => [...prev, result]);
      
      // Set the newly created agencia as the selected one
      handleChangeNewRegistro('agencia_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Agência criada com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewAgencia({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar agência:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar agência',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the agencia creation dialog
  const handleAgenciaSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewAgencia(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  // Add a handle function for anunciante creation
  const handleCreateAnunciante = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newAnunciante.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do anunciante é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await AnunciantesService.createAnunciante(newAnunciante);
      setOpenCreateAnuncianteDialog(false);
      
      // Add the new anunciante to the available anunciantes
      setAnunciantesDisponiveis(prev => [...prev, result]);
      
      // Set the newly created anunciante as the selected one
      handleChangeNewRegistro('anunciante_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Anunciante criado com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewAnunciante({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar anunciante:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar anunciante',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the anunciante creation dialog
  const handleAnuncianteSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewAnunciante(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  // Add a handle function for categoria creation
  const handleCreateCategoria = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newCategoria.nome) {
        setSnackbar({
          open: true,
          message: 'Nome da categoria é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await CategoriasService.createCategoria(newCategoria);
      setOpenCreateCategoriaDialog(false);
      
      // Add the new categoria to the available categorias
      setCategoriasDisponiveis(prev => [...prev, result]);
      
      // Set the newly created categoria as the selected one
      handleChangeNewRegistro('categoria_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Categoria criada com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewCategoria({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar categoria:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar categoria',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the categoria creation dialog
  const handleCategoriaSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewCategoria(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  // Add a handle function for setor creation
  const handleCreateSetor = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newSetor.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do setor é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await SetoresService.createSetor(newSetor);
      setOpenCreateSetorDialog(false);
      
      // Add the new setor to the available setores
      setSetoresDisponiveis(prev => [...prev, result]);
      
      // Set the newly created setor as the selected one
      handleChangeNewRegistro('setor_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Setor criado com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewSetor({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar setor:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar setor',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the setor creation dialog
  const handleSetorSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewSetor(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  // Add a handle function for item creation
  const handleCreateItem = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newItem.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do item é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await ItensService.createItem(newItem);
      setOpenCreateItemDialog(false);
      
      // Add the new item to the available items
      setItensDisponiveis(prev => [...prev, result]);
      
      // Set the newly created item as the selected one
      handleChangeNewRegistro('item_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Item criado com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewItem({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar item:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar item',
        severity: 'error',
      });
    }
  };

  // Add a handle function for select change in the item creation dialog
  const handleItemSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewItem(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  // Add a handle function for programa creation
  const handleCreatePrograma = async () => {
    try {
      // Verificar campos obrigatórios
      if (!newPrograma.nome) {
        setSnackbar({
          open: true,
          message: 'Nome do programa é obrigatório',
          severity: 'error',
        });
        return;
      }

      const result = await ProgramasService.createPrograma(newPrograma);
      setOpenCreateProgramaDialog(false);
      
      // Add the new programa to the available programas
      setProgramasDisponiveis(prev => [...prev, result]);
      
      // Set the newly created programa as the selected one
      handleChangeNewRegistro('programa_id', result.id);
      
      setSnackbar({
        open: true,
        message: 'Programa criado com sucesso',
        severity: 'success',
      });
      
      // Reset the form
      setNewPrograma({ nome: '' });
      
    } catch (error) {
      console.error('Erro ao criar programa:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao criar programa',
        severity: 'error',
      });
    }
  };
  
  // Add a handle function for select change in the programa creation dialog
  const handleProgramaSelectChange = (event: SelectChangeEvent<number | string | undefined>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setNewPrograma(prev => ({
      ...prev,
      [event.target.name]: value
    }));
  };

  const handleAddToPendingList = async () => {
    // Validate the new registro has at least some required data
    if (!newRegistro.datahora || !newRegistro.titulo || !isValidDate(newRegistro.datahora)) {
      setSnackbar({
        open: true,
        message: 'Preencha os campos obrigatórios: Data/Hora válida e Título',
        severity: 'error',
      });
      return;
    }

    // Extract the date part for dataabsoluta
    const dataabsoluta = newRegistro.datahora ? newRegistro.datahora.substring(0, 10) : undefined;

    try {
      // Grava IMEDIATAMENTE no banco para que nada seja perdido em caso de
      // "Limpar Formulário", fechamento acidental ou queda de energia.
      const saved = await RegistrosService.createRegistro({ ...newRegistro, dataabsoluta });

      // Mantém a lista dos registros já salvos nesta sessão (para conferência)
      setPendingRegistros(prev => [...prev, saved]);

      // Limpa os campos que mudam a cada inserção, evitando herdar dado do
      // registro anterior. Inclui tudo que vem do vínculo da marca (item,
      // categoria, setor, anunciante, agência) — categoria entra junto por ser
      // derivada do item. Data, Hora, Emissora, Programa, Tipo e Mercado
      // permanecem para agilizar o próximo cadastro.
      //
      // Com "Manter informações" marcado, APENAS Marca, Título e Duração
      // escapam dessa limpeza; todo o resto é limpo igual.
      setNewRegistro(prev => ({
        ...prev,
        titulo: manterInfo ? prev.titulo : '',
        duracao: manterInfo ? prev.duracao : undefined,
        marca_id: manterInfo ? prev.marca_id : undefined,
        custo: undefined,
        anunciante_id: undefined,
        item_id: undefined,
        categoria_id: undefined,
        setor_id: undefined,
        agencia_id: undefined,
      }));
      // Se a marca foi limpa, zera também a priorização de itens daquela marca
      if (!manterInfo) {
        setMarcaItemIds(new Set());
      }

      setSnackbar({
        open: true,
        message: manterInfo
          ? 'Registro salvo — Marca, Título e Duração mantidos'
          : 'Registro salvo automaticamente',
        severity: 'success',
      });

      // Atualiza a listagem principal para refletir o novo registro
      fetchRegistros();
    } catch (error) {
      console.error('Erro ao salvar registro na lista:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar o registro. Nenhum dado foi perdido, tente novamente.',
        severity: 'error',
      });
    }
  };

  const handleCreateMultipleRegistros = async () => {
    try {
      setLoading(true);

      // Os itens de "Adicionar à Lista" já foram salvos no banco na hora.
      // Aqui salvamos apenas o formulário atual, caso ainda tenha um registro
      // preenchido que não foi adicionado à lista ("salvar manual pra garantir").
      if (newRegistro.titulo && newRegistro.datahora && isValidDate(newRegistro.datahora)) {
        const dataabsoluta = newRegistro.datahora.substring(0, 10);
        await RegistrosService.createRegistro({ ...newRegistro, dataabsoluta });
      }

      setSnackbar({
        open: true,
        message: 'Registros salvos com sucesso',
        severity: 'success',
      });

      // Limpa a lista da sessão e o formulário
      setPendingRegistros([]);
      setNewRegistro({});
      setMarcaItemIds(new Set());
      resetDataHoraPartes();

      // Recarrega os dados e volta para a listagem
      fetchRegistros();
      navigate('/registros');
    } catch (error) {
      console.error('Erro ao salvar registros:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar registros',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePendingRegistro = (index: number) => {
    const registro: any = pendingRegistros[index];
    askConfirm(
      'Excluir registro',
      'Este registro já foi salvo no banco. Tem certeza que deseja excluí-lo?',
      async () => {
        try {
          if (registro && registro.id) {
            await RegistrosService.deleteRegistro(registro.id);
          }
          setPendingRegistros(prev => prev.filter((_, i) => i !== index));
          fetchRegistros();
          setSnackbar({ open: true, message: 'Registro excluído', severity: 'success' });
        } catch (error) {
          console.error('Erro ao excluir registro salvo:', error);
          setSnackbar({ open: true, message: 'Erro ao excluir registro', severity: 'error' });
        }
      },
      'Excluir'
    );
  };

  // Há QUALQUER campo preenchido no formulário? (valida todos os campos, não só título/data)
  const formTemDados = () => Object.values(newRegistro).some((v) => {
    if (v === undefined || v === null || v === '') return false;
    if (typeof v === 'number' && isNaN(v)) return false;
    return true;
  });

  const handleRequestClearForm = () => {
    const limpar = () => { setNewRegistro({}); setMarcaItemIds(new Set()); resetDataHoraPartes(); };
    // Só pergunta se houver algo para perder; formulário vazio limpa direto.
    if (!formTemDados()) { limpar(); return; }
    askConfirm(
      'Limpar formulário',
      'Tem certeza que deseja limpar os campos do formulário? Os registros já adicionados à lista permanecem salvos.',
      limpar,
      'Limpar'
    );
  };

  const handleRequestCloseCreate = () => {
    const finishClose = () => {
      setNewRegistro({});
      setMarcaItemIds(new Set());
      setPendingRegistros([]);
      resetDataHoraPartes();
      navigate('/registros');
    };
    // Pergunta se QUALQUER campo estiver preenchido (não só título+data).
    if (formTemDados()) {
      askConfirm(
        'Fechar cadastro',
        'Há dados preenchidos no formulário que ainda não foram adicionados à lista. Deseja fechar mesmo assim? (Os registros já adicionados continuam salvos.)',
        finishClose,
        'Fechar'
      );
    } else {
      finishClose();
    }
  };

  return (
    <SidebarLayout>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100%',
        overflowX: 'hidden'
      }}>
        {!isNovoPage && (<>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Registros
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Gerencie os registros de monitoramento de mídia.
          </Typography>
        </Box>

        {/* Filtros */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              Filtros
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={handleClearFilters}
                startIcon={<DataOffIcon />}
                sx={{ borderRadius: 2 }}
              >
                Limpar
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterIcon />}
                sx={{ borderRadius: 2 }}
              >
                {showFilters ? 'Menos filtros' : 'Mais filtros'}
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Título"
                variant="outlined"
                size="small"
                value={filter.titulo || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('titulo', e.target.value)}
                InputProps={{
                  sx: { 
                    borderRadius: 2,
                    height: FILTER_ELEMENT_HEIGHT 
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data início"
                type="datetime-local"
                value={filter.datahora_inicio || ''}
                onChange={(e) => handleFilterChange('datahora_inicio', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { 
                    borderRadius: 2,
                    height: FILTER_ELEMENT_HEIGHT,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.default, 0.8),
                    },
                    transition: 'all 0.2s ease',
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="Data e hora de início">
                        <Box component="span" sx={{ color: theme.palette.primary.main }}>
                          <TimelineIcon fontSize="small" />
                        </Box>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Data fim"
                type="datetime-local"
                value={filter.datahora_fim || ''}
                onChange={(e) => handleFilterChange('datahora_fim', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { 
                    borderRadius: 2,
                    height: FILTER_ELEMENT_HEIGHT,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.background.default, 0.8),
                    },
                    transition: 'all 0.2s ease',
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="Data e hora de fim">
                        <Box component="span" sx={{ color: theme.palette.primary.main }}>
                          <TimelineIcon fontSize="small" />
                        </Box>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {showFilters && (
              <>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[
                        {id: '', nome: 'Todos'},
                        {id: 'A', nome: 'A - Inicio/Encerramento Controle'},
                        {id: 'a', nome: 'a - Patrocício de Vinheta de Programa'},
                        {id: 'N', nome: 'N - Comercial'},
                        {id: 'M', nome: 'M - Merchandising'},
                        {id: 'P', nome: 'P - Programa'},
                        {id: 'p', nome: 'p - Patrocínio de Vinheta de Passagem'},
                        {id: 'I', nome: 'I - Interprograma'},
                        {id: 'V', nome: 'V - Vinheta'},
                        {id: 'v', nome: 'v - Patrocínio de Vinheta'},
                        {id: 'X', nome: 'X - Eventos'},
                        {id: 'x', nome: 'x - Patrocínio de Eventos'},
                        {id: 'Z', nome: 'Z - Comercial Político'},
                        {id: 'C', nome: 'C - Chamada'},
                        {id: 'c', nome: 'c - Patrocínio de Chamada'},
                        {id: 'B', nome: 'B - Break'},
                        {id: 'b', nome: 'b - Patrocínio de Boletim'},
                        {id: 'T', nome: 'T - Top'},
                        {id: 't', nome: 't - Patrocínio de Top'},
                        {id: 'S', nome: 'S - Subprograma'},
                        {id: 'Y', nome: 'Y - Encerramento de Subprograma'},
                        {id: 'Q', nome: 'Q - Quadro'},
                        {id: 'E', nome: 'E - Encerramento do Quadro'},
                        {id: 'e', nome: 'e - Patrocínio de Encerramento de Programa'},
                        {id: 'F', nome: 'F - Falha Técnica de Controle'},
                        {id: 'R', nome: 'R - Retorno de Falha Técnica'},
                        {id: 'q', nome: 'q - Patrocínio de Vinheta de Quadro'}
                      ]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Tipo" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT
                            }
                          }}
                        />
                      )}
                      value={filter.tipo ? {id: filter.tipo, nome: filter.tipo === '' ? 'Todos' : 
                        [
                          {id: 'A', nome: 'A - Inicio/Encerramento Controle'},
                          {id: 'a', nome: 'a - Patrocício de Vinheta de Programa'},
                          {id: 'N', nome: 'N - Comercial'},
                          {id: 'M', nome: 'M - Merchandising'},
                          {id: 'P', nome: 'P - Programa'},
                          {id: 'p', nome: 'p - Patrocínio de Vinheta de Passagem'},
                          {id: 'I', nome: 'I - Interprograma'},
                          {id: 'V', nome: 'V - Vinheta'},
                          {id: 'v', nome: 'v - Patrocínio de Vinheta'},
                          {id: 'X', nome: 'X - Eventos'},
                          {id: 'x', nome: 'x - Patrocínio de Eventos'},
                          {id: 'Z', nome: 'Z - Comercial Político'},
                          {id: 'C', nome: 'C - Chamada'},
                          {id: 'c', nome: 'c - Patrocínio de Chamada'},
                          {id: 'B', nome: 'B - Break'},
                          {id: 'b', nome: 'b - Patrocínio de Boletim'},
                          {id: 'T', nome: 'T - Top'},
                          {id: 't', nome: 't - Patrocínio de Top'},
                          {id: 'S', nome: 'S - Subprograma'},
                          {id: 'Y', nome: 'Y - Encerramento de Subprograma'},
                          {id: 'Q', nome: 'Q - Quadro'},
                          {id: 'E', nome: 'E - Encerramento do Quadro'},
                          {id: 'e', nome: 'e - Patrocínio de Encerramento de Programa'},
                          {id: 'F', nome: 'F - Falha Técnica de Controle'},
                          {id: 'R', nome: 'R - Retorno de Falha Técnica'},
                          {id: 'q', nome: 'q - Patrocínio de Vinheta de Quadro'}
                        ].find(item => item.id === filter.tipo)?.nome || filter.tipo
                      } : null}
                      onChange={(_, newValue) => handleFilterChange('tipo', newValue?.id || '')}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[
                        {id: '', nome: 'Todos'},
                        {id: 'Nacional', nome: 'Nacional'},
                        {id: 'Local', nome: 'Local'}
                      ]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Mercado" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                      )}
                      value={filter.mercado ? {id: filter.mercado, nome: filter.mercado} : null}
                      onChange={(_, newValue) => handleFilterChange('mercado', newValue?.id || '')}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todas'}, ...emissorasDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Emissora" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        />
                      )}
                      value={filter.emissora_id ? emissorasDisponiveis.find(e => e.id === filter.emissora_id) || null : null}
                      onChange={(_, newValue) => {
                        console.log('[RegistrosPage] Emissora selected:', newValue);
                        if (newValue?.id === '') {
                          // If "Todas" is selected, clear the filter
                          handleFilterChange('emissora_id', undefined);
                        } else {
                          // Set the single emissora ID
                          handleFilterChange('emissora_id', newValue?.id);
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todos'}, ...anunciantesDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Anunciante" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT,
                                display: 'flex',
                                alignItems: 'center'
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateAnuncianteDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar novo anunciante"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={filter.anunciante_id ? anunciantesDisponiveis.find(a => a.id === filter.anunciante_id) || null : null}
                      onChange={(_, newValue) => handleFilterChange('anunciante_id', newValue?.id === '' ? undefined : newValue?.id)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todas'}, ...marcasDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Marca" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            },
                            endAdornment: (
                              <React.Fragment>
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => {
                                      setNewMarca({ ...newMarca, nome: '' });
                                      setOpenCreateMarcaDialog(true);
                                    }}
                                    edge="end"
                                    size="small"
                                    sx={{ mr: 1 }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                      value={filter.marca_id ? marcasDisponiveis.find(marca => marca.id === filter.marca_id) || null : null}
                      onChange={(_, newValue) => {
                        if (newValue?.id === '-1') {
                          // Get the text from the newValue nome, removing the "Criar " prefix
                          const newMarcaName = newValue.nome.replace(/^Criar "(.*)"$/, '$1');
                          setNewMarca({
                            ...newMarca,
                            nome: newMarcaName
                          });
                          setOpenCreateMarcaDialog(true);
                          return;
                        }
                        handleFilterChange('marca_id', newValue?.id === '' ? undefined : newValue?.id);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todas'}, ...itensDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Item" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            },
                            endAdornment: (
                              <React.Fragment>
                                <IconButton 
                                  size="small" 
                                  tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setOpenCreateItemDialog(true);
                                  }}
                                  sx={{ mr: 1 }}
                                  title="Adicionar novo item"
                                >
                                  <AddIcon />
                                </IconButton>
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                      value={filter.item_id ? itensDisponiveis.find(item => item.id === filter.item_id) || null : null}
                      onChange={(_, newValue) => {
                        if (newValue && newValue.id === -1) {
                          // User clicked "Create new item"
                          const inputValue = newValue.nome.replace('Criar "', '').replace('"', '');
                          setNewItem({ nome: inputValue });
                          setOpenCreateItemDialog(true);
                          return;
                        }
                        handleFilterChange('item_id', newValue?.id === '' ? undefined : newValue?.id);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todos')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todos'}, ...setoresDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Setor" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            },
                            endAdornment: (
                              <React.Fragment>
                                <IconButton 
                                  size="small" 
                                  tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setOpenCreateSetorDialog(true);
                                  }}
                                  sx={{ mr: 1 }}
                                  title="Adicionar novo setor"
                                >
                                  <AddIcon />
                                </IconButton>
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                      value={filter.setor_id ? setoresDisponiveis.find(setor => setor.id === filter.setor_id) || null : null}
                      onChange={(_, newValue) => handleFilterChange('setor_id', newValue?.id === '' ? undefined : newValue?.id)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todos'}, ...programasDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Programa" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT,
                              display: 'flex',
                              alignItems: 'center'
                            },
                            endAdornment: (
                              <React.Fragment>
                                <IconButton 
                                  size="small" 
                                  tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setOpenCreateProgramaDialog(true);
                                  }}
                                  sx={{ mr: 1 }}
                                  title="Adicionar novo programa"
                                >
                                  <AddIcon />
                                </IconButton>
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                      value={filter.programa_id ? programasDisponiveis.find(programa => programa.id === filter.programa_id) || null : null}
                      onChange={(_, newValue) => handleFilterChange('programa_id', newValue?.id === '' ? undefined : newValue?.id)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todas'}, ...agenciasDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Agência" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT,
                                display: 'flex',
                                alignItems: 'center'
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateAgenciaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar nova agência"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={filter.agencia_id ? agenciasDisponiveis.find(a => a.id === filter.agencia_id) || null : null}
                      onChange={(_, newValue) => handleFilterChange('agencia_id', newValue?.id === '' ? undefined : newValue?.id)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { height: FILTER_ELEMENT_HEIGHT } }}>
                    <Autocomplete
                      options={[{id: '', nome: 'Todas'}, ...categoriasDisponiveis]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Categoria" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT,
                                display: 'flex',
                                alignItems: 'center'
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateCategoriaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar nova categoria"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={filter.categoria_id ? categoriasDisponiveis.find(c => c.id === filter.categoria_id) || null : null}
                      onChange={(_, newValue) => handleFilterChange('categoria_id', newValue?.id === '' ? undefined : newValue?.id)}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      filterOptions={(options, state) => {
                        const inputValue = state.inputValue.toLowerCase().trim();
                        const filtered = options.filter(option => 
                          option.nome.toLowerCase().includes(inputValue)
                        );
                        
                        // Add option to create new marca if not found and input value is not empty
                        if (inputValue !== '' && 
                            !filtered.some(x => x.nome.toLowerCase() === inputValue) && 
                            !filtered.some(x => x.nome === 'Todas')) {
                          filtered.push({
                            id: '-1' as any,
                            nome: `Criar "${state.inputValue}"`
                          });
                        }
                        
                        return filtered;
                      }}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                      ListboxComponent={ListboxComponent}
                    />
                  </FormControl>
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  console.log('Abrindo diálogo de criação, emissoras disponíveis:', emissorasDisponiveis);
                  console.log('Marcas disponíveis:', marcasDisponiveis);
                  
                  // Se não houver emissoras, tentar buscar novamente
                  if (!emissorasDisponiveis || emissorasDisponiveis.length === 0) {
                    console.log('Nenhuma emissora encontrada, buscando novamente...');
                    EmissorasService.getEmissoras({}, 0, 1000).then(data => {
                      console.log('Emissoras recebidas na requisição direta:', data);
                      setEmissorasDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar emissoras:', err));
                  }
                  
                  // Se não houver anunciantes, tentar buscar novamente
                  if (!anunciantesDisponiveis || anunciantesDisponiveis.length === 0) {
                    console.log('Nenhum anunciante encontrado, buscando novamente...');
                    AnunciantesService.getAllAnunciantes().then(data => {
                      console.log('Anunciantes recebidos na requisição direta:', data);
                      setAnunciantesDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar anunciantes:', err));
                  }
                  
                  // Se não houver marcas, tentar buscar novamente
                  if (!marcasDisponiveis || marcasDisponiveis.length === 0) {
                    console.log('Nenhuma marca encontrada, buscando novamente...');
                    MarcasService.getMarcas({}, 0, 50000).then(data => { // Increased limit to 50000
                      console.log('Marcas recebidas na requisição direta:', data);
                      setMarcasDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar marcas:', err));
                  }
                  
                  // Se não houver itens, tentar buscar novamente
                  if (!itensDisponiveis || itensDisponiveis.length === 0) {
                    console.log('Nenhum item encontrado, buscando novamente...');
                    ItensService.getAllItens().then(data => {
                      console.log('Itens recebidos na requisição direta:', data);
                      setItensDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar itens:', err));
                  }
                  
                  // Se não houver setores, tentar buscar novamente
                  if (!setoresDisponiveis || setoresDisponiveis.length === 0) {
                    console.log('Nenhum setor encontrado, buscando novamente...');
                    SetoresService.getSetores({}, 0, 1000).then(data => {
                      console.log('Setores recebidos na requisição direta:', data);
                      setSetoresDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar setores:', err));
                  }
                  
                  // Se não houver programas, tentar buscar novamente
                  if (!programasDisponiveis || programasDisponiveis.length === 0) {
                    console.log('Nenhum programa encontrado, buscando novamente...');
                    ProgramasService.getAllProgramas().then(data => {
                      console.log('Programas recebidos na requisição direta:', data);
                      setProgramasDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar programas:', err));
                  }
                  
                  // Se não houver agências, tentar buscar novamente
                  if (!agenciasDisponiveis || agenciasDisponiveis.length === 0) {
                    console.log('Nenhuma agência encontrada, buscando novamente...');
                    AgenciasService.getAllAgencias().then(data => {
                      console.log('Agências recebidas na requisição direta:', data);
                      setAgenciasDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar agências:', err));
                  }
                  
                  // Se não houver categorias, tentar buscar novamente
                  if (!categoriasDisponiveis || categoriasDisponiveis.length === 0) {
                    console.log('Nenhuma categoria encontrada, buscando novamente...');
                    CategoriasService.getAllCategorias().then(data => {
                      console.log('Categorias recebidas na requisição direta:', data);
                      setCategoriasDisponiveis(Array.isArray(data) ? data : []);
                    }).catch(err => console.error('Erro ao buscar categorias:', err));
                  }
                  
                  // Agora o cadastro é uma página com URL própria
                  navigate('/registros/novo');
                }}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                  px: 3,
                  py: 1,
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                  }
                }}
              >
                Novo Registro
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de Registros */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          ) : registros.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="textSecondary">
                Nenhum registro encontrado
              </Typography>
            </Box>
          ) : (
            <Fade in={!loading}>
              <TableContainer sx={{ width: '100%', overflow: 'auto' }}>
                <Table sx={{ 
                  width: '100%',
                  tableLayout: 'fixed',
                  [`& .${tableCellClasses.root}`]: {
                    borderBottom: '1px solid',
                    borderColor: 'rgba(0, 0, 0, 0.06)',
                  },
                  [`& .${tableCellClasses.head}`]: {
                    fontWeight: 'bold',
                    backgroundColor: alpha(theme.palette.primary.main, 0.03),
                  },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width="15%" sx={{ 
                        py: 2, 
                        borderTopLeftRadius: 2, 
                        borderBottomLeftRadius: 0,
                      }}>Data/Hora</TableCell>
                      <TableCell width="20%">Título</TableCell>
                      <TableCell width="10%">Emissora</TableCell>
                      <TableCell width="10%">Anunciante</TableCell>
                      <TableCell width="10%">Marca</TableCell>
                      <TableCell width="10%">Item</TableCell>
                      <TableCell width="10%">Categoria</TableCell>
                      <TableCell width="10%">Tipo</TableCell>
                      <TableCell width="10%">Precisão</TableCell>
                      <TableCell width="14%" sx={{ 
                        borderTopRightRadius: 2, 
                        borderBottomRightRadius: 0, 
                        textAlign: 'center'
                      }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {registros.map((registro: Registro) => (
                      <TableRow 
                        key={registro.id}
                        hover
                        sx={{ 
                          transition: 'all 0.2s',
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            cursor: 'pointer'
                          }
                        }}
                        onClick={(e) => {
                          // Prevent navigation if clicking on action buttons
                          if (!(e.target as HTMLElement).closest('button')) {
                            navigate(`/registros/${registro.id}`);
                          }
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.875rem' }}>
                          {formatDateTimeDisplay(registro.datahora)}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 'medium',
                            maxWidth: '250px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {registro.titulo}
                        </TableCell>
                        <TableCell>
                          {registro.emissora?.nome || 
                           emissorasDisponiveis.find(e => e.id === registro.emissora_id)?.nome || 
                           '-'}
                        </TableCell>
                        <TableCell>
                          {registro.anunciante?.nome || 
                           anunciantesDisponiveis.find(a => a.id === registro.anunciante_id)?.nome || 
                           '-'}
                        </TableCell>
                        <TableCell>
                          {registro.marca?.nome || 
                           marcasDisponiveis.find(m => m.id === registro.marca_id)?.nome || 
                           '-'}
                        </TableCell>
                        <TableCell>{registro.item_id && itensDisponiveis?.find(i => i.id === registro.item_id)?.nome || '-'}</TableCell>
                        <TableCell>{registro.categoria_id && categoriasDisponiveis?.find(c => c.id === registro.categoria_id)?.nome || '-'}</TableCell>
                        <TableCell>{registro.tipo || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${Number(registro.precisao).toFixed(2)}%`} 
                            size="small"
                            sx={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 'medium',
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}>
                          <Tooltip title="Ver Detalhes">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/registros/${registro.id}`);
                              }}
                              sx={{ 
                                mr: 1,
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(registro);
                              }}
                              sx={{ 
                                mr: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteDialog(registro);
                              }}
                              sx={{ 
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Fade>
          )}
          <Box sx={{ mt: 3 }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
            />
          </Box>
        </Paper>

        </>)}

        {/* Página de Novo Registro — rota /registros/novo (antes era um modal) */}
        {isNovoPage && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            // NÃO usar overflow:'hidden' aqui: isso transforma o Paper em
            // container de rolagem e mata o position:sticky da barra de botões.
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            width: '100%'
          }}
        >
          <Box sx={{
            bgcolor: theme.palette.primary.main,
            color: 'white',
            py: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            fontSize: '1.2rem',
            fontWeight: 600,
            borderRadius: '8px 8px 0 0'
          }}>
            <AddIcon /> Novo Registro
          </Box>

          <Box sx={{ p: 3, pt: 4, bgcolor: alpha(theme.palette.background.paper, 0.97) }}>
            <Box sx={{
              pr: 1,
              // Padronização visual do formulário: todos os campos com a MESMA
              // altura e sem margens próprias. Antes uns eram size="medium" e
              // outros size="small", e alguns tinham mt/mb individuais — daí os
              // tamanhos diferentes e o desalinhamento entre as duas colunas.
              '& .MuiFormControl-root': { mt: 0, mb: 0, width: '100%' },
              '& .MuiOutlinedInput-root': { borderRadius: 2, minHeight: FILTER_ELEMENT_HEIGHT },
              // Texto de erro (ex.: "Hora é obrigatória") não empurra mais a linha
              '& .MuiFormHelperText-root': { position: 'absolute', top: '100%', mt: 0.25, ml: 1.5 }
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Data"
                    type="date"
                    value={dataParte}
                    onChange={(e) => handleDataParte(e.target.value)}
                    onClick={(e) => {
                      // Ao clicar na Data, abre o calendário nativo (como antes)
                      const input = (e.currentTarget as HTMLElement).querySelector('input') as HTMLInputElement | null;
                      try { (input as any)?.showPicker?.(); } catch { /* sem suporte a showPicker */ }
                    }}
                    required
                    error={newRegistro.datahora !== undefined && dataParte === ''}
                    helperText={newRegistro.datahora !== undefined && dataParte === '' ? 'Data é obrigatória' : ''}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      sx: {
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: '0 0 0 2px rgba(0,0,0,0.05)' },
                        '&.Mui-focused': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Custo"
                    type="number"
                    value={newRegistro.custo || ''}
                    onChange={(e) => handleChangeNewRegistro('custo', parseFloat(e.target.value))}
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      },
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Qualidade"
                    value=""
                    disabled
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gênero"
                    value=""
                    disabled
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={marcasDisponiveis}
                      getOptionLabel={(option) => (option.nome || '').trim()}
                      renderInput={(params: AutocompleteRenderInputParams) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Marca" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT,
                                display: 'flex',
                                alignItems: 'center'
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateMarcaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar nova marca"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={marcasDisponiveis.find(m => m.id === newRegistro.marca_id) || null}
                      onChange={async (_, newValue) => {
                        const marcaId = newValue?.id ?? null;
                        handleChangeNewRegistro('marca_id', marcaId || null);

                        // Ao trocar de marca, recarrega os itens vinculados (para o
                        // dropdown de Item priorizar os itens daquela marca no topo).
                        if (typeof marcaId === 'number' && marcaId > 0) {
                          RegistrosService.getItensPorMarca(marcaId).then(itens => {
                            setMarcaItemIds(new Set(itens.map(i => i.id)));
                          });
                        } else {
                          setMarcaItemIds(new Set());
                        }

                        // Autopreenchimento: ao escolher a marca, buscar no histórico
                        // o setor/item/mercado etc. e preencher apenas os campos ainda
                        // vazios (sem sobrescrever o que o usuário já digitou).
                        if (typeof marcaId === 'number' && marcaId > 0) {
                          const sugestao = await RegistrosService.getSugestaoMarca(marcaId);
                          const campos: Array<keyof RegistroCreate> = [
                            'setor_id', 'item_id', 'categoria_id', 'anunciante_id',
                            'agencia_id', 'mercado', 'genero', 'tipo', 'duracao',
                          ];
                          let preencheu = false;
                          setNewRegistro(prev => {
                            const merged: any = { ...prev, marca_id: marcaId };
                            campos.forEach((c) => {
                              const atual = merged[c];
                              const valor = (sugestao as any)[c];
                              if ((atual === undefined || atual === null || atual === '') &&
                                  valor !== undefined && valor !== null && valor !== '') {
                                merged[c] = valor;
                                preencheu = true;
                              }
                            });
                            return merged;
                          });
                          if (preencheu) {
                            setSnackbar({
                              open: true,
                              message: 'Campos preenchidos automaticamente pela marca',
                              severity: 'success',
                            });
                          }
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('marca')}
                      filterOptions={filtroDoCampo(
                        'marca',
                        () => marcasDisponiveis.find(m => m.id === newRegistro.marca_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={itensDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Item" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateItemDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar novo item"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={itensDisponiveis.find(i => i.id === newRegistro.item_id) || null}
                      groupBy={marcaItemIds.size > 0
                        ? (option: any) => (marcaItemIds.has(option.id) ? '★ Itens desta marca' : 'Todos os itens')
                        : undefined}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('item_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('item')}
                      filterOptions={(options, state) => {
                        const termo = termoBusca(
                          'item',
                          state,
                          itensDisponiveis.find(i => i.id === newRegistro.item_id)?.nome
                        );
                        const filtrados = filtrarPorTermo(options, termo, 200);

                        // Prioriza os itens vinculados à marca no topo (o resto fica embaixo).
                        // A ordenação é estável, então dentro de cada grupo a relevância
                        // da busca é preservada.
                        if (marcaItemIds.size > 0) {
                          filtrados.sort((a, b) => {
                            const av = marcaItemIds.has(a.id) ? 0 : 1;
                            const bv = marcaItemIds.has(b.id) ? 0 : 1;
                            return av - bv;
                          });
                        }

                        if (
                          normalizarTexto(termo) !== '' &&
                          !filtrados.some(x => normalizarTexto(x.nome) === normalizarTexto(termo))
                        ) {
                          filtrados.push({ id: '-1' as any, nome: `Criar "${termo.trim()}"` });
                        }

                        return filtrados;
                      }}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={tituloSugestoes}
                    inputValue={newRegistro.titulo || ''}
                    onOpen={() => fetchTituloSugestoes(newRegistro.titulo || '')}
                    onInputChange={(_, value, reason) => {
                      handleChangeNewRegistro('titulo', value);
                      if (reason === 'input') {
                        fetchTituloSugestoes(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Título"
                        InputProps={{
                          ...params.InputProps,
                          sx: {
                            borderRadius: 2,
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                            },
                            '&.Mui-focused': {
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                            }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={programasDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Programa" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateProgramaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar novo programa"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={programasDisponiveis.find(p => p.id === newRegistro.programa_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('programa_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('programa')}
                      filterOptions={filtroDoCampo(
                        'programa',
                        () => programasDisponiveis.find(p => p.id === newRegistro.programa_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hora"
                    type="time"
                    value={horaParte}
                    onChange={(e) => handleHoraParte(e.target.value)}
                    // step=1 faz o campo mostrar os segundos (HH:mm:ss) no mesmo campo
                    inputProps={{ step: 1 }}
                    required
                    error={newRegistro.datahora !== undefined && horaParte === ''}
                    helperText={newRegistro.datahora !== undefined && horaParte === '' ? 'Hora é obrigatória' : ''}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      sx: {
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': { boxShadow: '0 0 0 2px rgba(0,0,0,0.05)' },
                        '&.Mui-focused': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` },
                        // Remove o seletor de relógio: a Hora é só digitada
                        '& input::-webkit-calendar-picker-indicator': { display: 'none' }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Duração (segundos)"
                    type="number"
                    value={newRegistro.duracao || ''}
                    onChange={(e) => handleChangeNewRegistro('duracao', parseInt(e.target.value))}
                    InputProps={{
                      sx: { 
                        borderRadius: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={agenciasDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Agência" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateAgenciaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar nova agência"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={agenciasDisponiveis.find(a => a.id === newRegistro.agencia_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('agencia_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('agencia')}
                      filterOptions={filtroDoCampo(
                        'agencia',
                        () => agenciasDisponiveis.find(a => a.id === newRegistro.agencia_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={categoriasDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Categoria" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { 
                                borderRadius: 2,
                                height: FILTER_ELEMENT_HEIGHT
                              },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateCategoriaDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar nova categoria"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={categoriasDisponiveis.find(c => c.id === newRegistro.categoria_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('categoria_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('categoria')}
                      filterOptions={filtroDoCampo(
                        'categoria',
                        () => categoriasDisponiveis.find(c => c.id === newRegistro.categoria_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={emissorasDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Emissora" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 2 }
                          }}
                        />
                      )}
                      value={emissorasDisponiveis.find(e => e.id === newRegistro.emissora_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('emissora_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('emissora')}
                      filterOptions={filtroDoCampo(
                        'emissora',
                        () => emissorasDisponiveis.find(e => e.id === newRegistro.emissora_id)?.nome,
                        // Emissora não tem cadastro rápido aqui: sem opção "Criar ..."
                        { permitirCriar: false, limite: 200 }
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={[
                        { id: 'A', nome: 'A - Inicio/Encerramento Controle' },
                        { id: 'a', nome: 'a - Patrocício de Vinheta de Programa' },
                        { id: 'N', nome: 'N - Comercial' },
                        { id: 'M', nome: 'M - Merchandising' },
                        { id: 'P', nome: 'P - Programa' },
                        { id: 'p', nome: 'p - Patrocínio de Vinheta de Passagem' },
                        { id: 'I', nome: 'I - Interprograma' },
                        { id: 'V', nome: 'V - Vinheta' },
                        { id: 'v', nome: 'v - Patrocínio de Vinheta' },
                        { id: 'X', nome: 'X - Eventos' },
                        { id: 'x', nome: 'x - Patrocínio de Eventos' },
                        { id: 'Z', nome: 'Z - Comercial Político' },
                        { id: 'C', nome: 'C - Chamada' },
                        { id: 'c', nome: 'c - Patrocínio de Chamada' },
                        { id: 'B', nome: 'B - Break' },
                        { id: 'b', nome: 'b - Patrocínio de Boletim' },
                        { id: 'T', nome: 'T - Top' },
                        { id: 't', nome: 't - Patrocínio de Top' },
                        { id: 'S', nome: 'S - Subprograma' },
                        { id: 'Y', nome: 'Y - Encerramento de Subprograma' },
                        { id: 'Q', nome: 'Q - Quadro' },
                        { id: 'E', nome: 'E - Encerramento do Quadro' },
                        { id: 'e', nome: 'e - Patrocínio de Encerramento de Programa' },
                        { id: 'F', nome: 'F - Falha Técnica de Controle' },
                        { id: 'R', nome: 'R - Retorno de Falha Técnica' },
                        { id: 'q', nome: 'q - Patrocínio de Vinheta de Quadro' }
                      ]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Tipo" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 2 }
                          }}
                        />
                      )}
                      value={newRegistro.tipo ? { id: newRegistro.tipo, nome: newRegistro.tipo === 'A' ? 'A - Inicio/Encerramento Controle' : 
                        newRegistro.tipo === 'a' ? 'a - Patrocício de Vinheta de Programa' : 
                        newRegistro.tipo === 'N' ? 'N - Comercial' : 
                        newRegistro.tipo === 'M' ? 'M - Merchandising' : 
                        newRegistro.tipo === 'P' ? 'P - Programa' : 
                        newRegistro.tipo === 'p' ? 'p - Patrocínio de Vinheta de Passagem' : 
                        newRegistro.tipo === 'I' ? 'I - Interprograma' : 
                        newRegistro.tipo === 'V' ? 'V - Vinheta' : 
                        newRegistro.tipo === 'v' ? 'v - Patrocínio de Vinheta' : 
                        newRegistro.tipo === 'X' ? 'X - Eventos' : 
                        newRegistro.tipo === 'x' ? 'x - Patrocínio de Eventos' : 
                        newRegistro.tipo === 'Z' ? 'Z - Comercial Político' : 
                        newRegistro.tipo === 'C' ? 'C - Chamada' : 
                        newRegistro.tipo === 'c' ? 'c - Patrocínio de Chamada' : 
                        newRegistro.tipo === 'B' ? 'B - Break' : 
                        newRegistro.tipo === 'b' ? 'b - Patrocínio de Boletim' : 
                        newRegistro.tipo === 'T' ? 'T - Top' : 
                        newRegistro.tipo === 't' ? 't - Patrocínio de Top' : 
                        newRegistro.tipo === 'S' ? 'S - Subprograma' : 
                        newRegistro.tipo === 'Y' ? 'Y - Encerramento de Subprograma' : 
                        newRegistro.tipo === 'Q' ? 'Q - Quadro' : 
                        newRegistro.tipo === 'E' ? 'E - Encerramento do Quadro' : 
                        newRegistro.tipo === 'e' ? 'e - Patrocínio de Encerramento de Programa' : 
                        newRegistro.tipo === 'F' ? 'F - Falha Técnica de Controle' : 
                        newRegistro.tipo === 'R' ? 'R - Retorno de Falha Técnica' : 
                        newRegistro.tipo === 'q' ? 'q - Patrocínio de Vinheta de Quadro' : newRegistro.tipo
                      } : null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('tipo', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={anunciantesDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => {
                        const { InputProps, ...rest } = params;
                        return (
                          <TextField 
                            {...rest} 
                            label="Anunciante" 
                            variant="outlined"
                            size="small"
                            InputProps={{
                              ...InputProps,
                              sx: { borderRadius: 2 },
                              endAdornment: (
                                <React.Fragment>
                                  <IconButton 
                                    size="small" 
                                    tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                      e.stopPropagation();
                                      setOpenCreateAnuncianteDialog(true);
                                    }}
                                    sx={{ mr: 1 }}
                                    title="Adicionar novo anunciante"
                                  >
                                    <AddIcon />
                                  </IconButton>
                                  {InputProps.endAdornment}
                                </React.Fragment>
                              )
                            }}
                          />
                        );
                      }}
                      value={anunciantesDisponiveis.find(a => a.id === newRegistro.anunciante_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('anunciante_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('anunciante')}
                      filterOptions={filtroDoCampo(
                        'anunciante',
                        () => anunciantesDisponiveis.find(a => a.id === newRegistro.anunciante_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={setoresDisponiveis}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Setor" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { 
                              borderRadius: 2,
                              height: FILTER_ELEMENT_HEIGHT
                            },
                            endAdornment: (
                              <React.Fragment>
                                <IconButton 
                                  size="small" 
                                  tabIndex={-1}
                                    onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setOpenCreateSetorDialog(true);
                                  }}
                                  sx={{ mr: 1 }}
                                  title="Adicionar novo setor"
                                >
                                  <AddIcon />
                                </IconButton>
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            )
                          }}
                        />
                      )}
                      value={setoresDisponiveis.find(s => s.id === newRegistro.setor_id) || null}
                      onChange={(_, newValue) => {
                        handleChangeNewRegistro('setor_id', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      onInputChange={registrarBusca('setor')}
                      filterOptions={filtroDoCampo(
                        'setor',
                        () => setoresDisponiveis.find(s => s.id === newRegistro.setor_id)?.nome
                      )}
                      freeSolo={false}
                      disablePortal={false}
                      selectOnFocus
                      clearOnBlur
                      handleHomeEndKeys
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={[{ id: 'Local', nome: 'Local' }, { id: 'Nacional', nome: 'Nacional' }]}
                      getOptionLabel={(option) => option.nome}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Mercado" 
                          variant="outlined"
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            sx: { borderRadius: 2 }
                          }}
                        />
                      )}
                      value={newRegistro.mercado ? { id: newRegistro.mercado, nome: newRegistro.mercado } as any : null}
                      onChange={(_, newValue: any) => {
                        handleChangeNewRegistro('mercado', newValue?.id || null);
                      }}
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="PI Number"
                    value={newRegistro.pi_number || ''}
                    onChange={(e) => handleChangeNewRegistro('pi_number', e.target.value)}
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    label="Considerações"
                    value={newRegistro.consideracoes || ''}
                    onChange={(e) => handleChangeNewRegistro('consideracoes', e.target.value)}
                    InputProps={{
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  {/* "Adicionar à Lista" fica ao lado do "Manter informações":
                      os dois são a mesma ação do dia a dia (lançar a próxima
                      inserção), então ficam juntos, fora da barra do rodapé. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={manterInfo}
                          onChange={(e) => {
                            setManterInfo(e.target.checked);
                            localStorage.setItem('registros_manter_info', e.target.checked ? '1' : '0');
                          }}
                        />
                      }
                      label="Manter informações (Marca, Título e Duração continuam preenchidos ao adicionar à lista)"
                      sx={{ mr: 0 }}
                    />
                    <Button
                      onClick={handleAddToPendingList}
                      variant="outlined"
                      color="primary"
                      startIcon={<AddIcon />}
                      sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                      Adicionar à Lista
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>

          {/* Barra de botões — fica GRUDADA no rodapé da tela (position:sticky),
              sempre no mesmo lugar, mesmo com o formulário rolando. A lista de
              registros salvos na sessão vem logo ABAIXO desta barra. */}
          <Box sx={{
            px: 3, py: 2,
            bgcolor: theme.palette.background.paper,
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            position: 'sticky',
            bottom: 0,
            zIndex: 3,
            boxShadow: '0 -4px 12px rgba(0,0,0,0.18)'
          }}>
            <Box>
              <Button
                onClick={handleRequestCloseCreate}
                variant="outlined"
                sx={{ borderRadius: 2, mr: 1 }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleRequestClearForm}
                variant="outlined"
                color="secondary"
                sx={{ borderRadius: 2 }}
              >
                Limpar Formulário
              </Button>
            </Box>
            
            <Box>
              <Button
                onClick={handleCreateMultipleRegistros}
                variant="contained"
                sx={{ 
                  borderRadius: 2,
                  boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)'
                  }
                }}
              >
                {pendingRegistros.length > 0 ? `Salvar ${pendingRegistros.length} ${pendingRegistros.length === 1 ? 'Registro' : 'Registros'}` : 'Salvar Registro'}
              </Button>
            </Box>
          </Box>

          {/* Registros já salvos nesta sessão — ABAIXO da barra de botões */}
          {pendingRegistros.length > 0 && (
            <Box sx={{ px: 3, pt: 2, pb: 3, bgcolor: alpha(theme.palette.background.paper, 0.97) }}>
              <Divider sx={{ mb: 1.5 }}>
                <Chip
                  size="small"
                  label={
                    pendingRegistros.length === 1
                      ? '1 registro salvo nesta sessão'
                      : `${pendingRegistros.length} registros salvos nesta sessão`
                  }
                  color="success"
                  sx={{ fontWeight: 'medium' }}
                />
              </Divider>

              {/* Lista COMPLETA do que foi salvo nesta sessão, do mais recente
                  para o mais antigo. Como agora ela fica abaixo dos botões, não
                  rouba mais o espaço vertical dos campos do formulário. */}
              <List
                sx={{
                  bgcolor: alpha(theme.palette.primary.light, 0.05),
                  borderRadius: 2,
                  p: 1,
                  maxHeight: '45vh',
                  overflowY: 'auto'
                }}
              >
                {pendingRegistros.map((registro, idx) => ({ registro, idx })).reverse().map(({ registro, idx }) => (
                  <ListItem
                    key={(registro as any).id ?? idx}
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Editar este registro">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditDialog(registro as any)}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir este registro">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemovePendingRegistro(idx)}
                            sx={{
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                    sx={{
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: 'background.paper',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <ListItemText
                      primary={registro.titulo || 'Sem título'}
                      secondary={
                        <>
                          {registro.emissora_id && emissorasDisponiveis.find(e => e.id === registro.emissora_id)?.nome}
                          {registro.datahora && ` • ${formatDateTimeDisplay(registro.datahora)}`}
                          {registro.marca_id && ` • ${marcasDisponiveis.find(m => m.id === registro.marca_id)?.nome}`}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
        )}

        {/* Diálogo de confirmação genérico (limpar/fechar/excluir) */}
        <Dialog open={confirmDialog.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 600 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 1 }}>{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeConfirm} variant="outlined" sx={{ borderRadius: 2 }}>
              Cancelar
            </Button>
            <Button
              onClick={() => { const fn = confirmDialog.onConfirm; closeConfirm(); fn(); }}
              variant="contained"
              color="error"
              sx={{ borderRadius: 2 }}
            >
              {confirmDialog.confirmLabel}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog para Confirmar Exclusão */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          PaperProps={{
            sx: { borderRadius: 2 }
          }}
        >
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Tem certeza que deseja remover o registro{' '}
              <strong>#{currentRegistro?.id}</strong>?
            </Typography>
            {currentRegistro?.titulo && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Título: {currentRegistro.titulo}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setOpenDeleteDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button 
              color="error" 
              onClick={handleDeleteRegistro}
              variant="contained"
              sx={{ borderRadius: 2 }}
            >
              Remover
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para mensagens */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
      
      {/* Add this new Dialog for creating marcas */}
      <Dialog
        open={openCreateMarcaDialog}
        onClose={() => setOpenCreateMarcaDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Marca</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newMarca.nome || ''}
                onChange={(e) => setNewMarca({ ...newMarca, nome: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateMarcaDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateMarca}
            disabled={!newMarca.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Agencia */}
      <Dialog
        open={openCreateAgenciaDialog}
        onClose={() => setOpenCreateAgenciaDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Agência</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newAgencia.nome || ''}
                onChange={(e) => setNewAgencia({ ...newAgencia, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-agencia-principal-label">Agência Principal</InputLabel>
                <Select
                  labelId="new-agencia-principal-label"
                  label="Agência Principal"
                  name="principal_id"
                  value={newAgencia.principal_id || ''}
                  onChange={handleAgenciaSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {agenciasPrincipais.map((agencia) => (
                    <MenuItem key={agencia.id} value={agencia.id}>
                      {agencia.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateAgenciaDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateAgencia}
            disabled={!newAgencia.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Anunciante */}
      <Dialog
        open={openCreateAnuncianteDialog}
        onClose={() => setOpenCreateAnuncianteDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Anunciante</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newAnunciante.nome || ''}
                onChange={(e) => setNewAnunciante({ ...newAnunciante, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-anunciante-principal-label">Anunciante Principal</InputLabel>
                <Select
                  labelId="new-anunciante-principal-label"
                  label="Anunciante Principal"
                  name="principal_id"
                  value={newAnunciante.principal_id || ''}
                  onChange={handleAnuncianteSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {anunciantesPrincipais.map((anunciante) => (
                    <MenuItem key={anunciante.id} value={anunciante.id}>
                      {anunciante.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateAnuncianteDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateAnunciante}
            disabled={!newAnunciante.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Setor */}
      <Dialog
        open={openCreateSetorDialog}
        onClose={() => setOpenCreateSetorDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Setor</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newSetor.nome || ''}
                onChange={(e) => setNewSetor({ ...newSetor, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-setor-principal-label">Setor Principal</InputLabel>
                <Select
                  labelId="new-setor-principal-label"
                  label="Setor Principal"
                  name="principal_id"
                  value={newSetor.principal_id || ''}
                  onChange={handleSetorSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setoresPrincipais
                    .filter(setor => newSetor.nome ? setor.nome.toLowerCase() !== newSetor.nome.toLowerCase() : true)
                    .map((setor) => (
                      <MenuItem key={setor.id} value={setor.id}>
                        {setor.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateSetorDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateSetor}
            disabled={!newSetor.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Categoria */}
      <Dialog
        open={openCreateCategoriaDialog}
        onClose={() => setOpenCreateCategoriaDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Nova Categoria</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newCategoria.nome || ''}
                onChange={(e) => setNewCategoria({ ...newCategoria, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-categoria-setor-label">Setor</InputLabel>
                <Select
                  labelId="new-categoria-setor-label"
                  label="Setor"
                  name="setor_id"
                  value={newCategoria.setor_id || ''}
                  onChange={handleCategoriaSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {setoresDisponiveis.map((setor) => (
                    <MenuItem key={setor.id} value={setor.id}>
                      {setor.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-categoria-principal-label">Categoria Principal</InputLabel>
                <Select
                  labelId="new-categoria-principal-label"
                  label="Categoria Principal"
                  name="principal_id"
                  value={newCategoria.principal_id || ''}
                  onChange={handleCategoriaSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categoriasPrincipais
                    .filter(categoria => newCategoria.nome ? categoria.nome.toLowerCase() !== newCategoria.nome.toLowerCase() : true)
                    .map((categoria) => (
                      <MenuItem key={categoria.id} value={categoria.id}>
                        {categoria.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateCategoriaDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateCategoria}
            disabled={!newCategoria.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Item */}
      <Dialog
        open={openCreateItemDialog}
        onClose={() => setOpenCreateItemDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Item</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newItem.nome || ''}
                onChange={(e) => setNewItem({ ...newItem, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-item-categoria-label">Categoria</InputLabel>
                <Select
                  labelId="new-item-categoria-label"
                  label="Categoria"
                  name="categoria_id"
                  value={newItem.categoria_id || ''}
                  onChange={handleItemSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {categoriasDisponiveis.map((categoria) => (
                    <MenuItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-item-principal-label">Item Principal</InputLabel>
                <Select
                  labelId="new-item-principal-label"
                  label="Item Principal"
                  name="principal_id"
                  value={newItem.principal_id || ''}
                  onChange={handleItemSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {itensPrincipais
                    .filter(item => newItem.nome ? item.nome.toLowerCase() !== newItem.nome.toLowerCase() : true)
                    .map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateItemDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreateItem}
            disabled={!newItem.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Dialog for creating a new Programa */}
      <Dialog
        open={openCreateProgramaDialog}
        onClose={() => setOpenCreateProgramaDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo Programa</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                required
                value={newPrograma.nome || ''}
                onChange={(e) => setNewPrograma({ ...newPrograma, nome: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mnemônico"
                name="mnemonico"
                value={newPrograma.mnemonico || ''}
                onChange={(e) => setNewPrograma({ ...newPrograma, mnemonico: e.target.value })}
                helperText="Código ou abreviação do programa"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="new-programa-principal-label">Programa Principal</InputLabel>
                <Select
                  labelId="new-programa-principal-label"
                  label="Programa Principal"
                  name="principal_id"
                  value={newPrograma.principal_id || ''}
                  onChange={handleProgramaSelectChange}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  {programasDisponiveis
                    .filter(programa => newPrograma.nome ? programa.nome.toLowerCase() !== newPrograma.nome.toLowerCase() : true)
                    .map((programa) => (
                      <MenuItem key={programa.id} value={programa.id}>
                        {programa.nome}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateProgramaDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleCreatePrograma}
            disabled={!newPrograma.nome}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.primary.main, 
          color: 'white',
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          fontSize: '1.2rem',
          fontWeight: 600
        }}>
          <EditIcon /> Editar Registro
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 4, bgcolor: alpha(theme.palette.background.paper, 0.97) }}>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Data/Hora"
                  type="datetime-local"
                  value={editRegistro.datahora || ''}
                  onChange={(e) => handleChangeEditRegistro('datahora', e.target.value)}
                  required
                  error={editRegistro.datahora !== undefined && (editRegistro.datahora === '' || !isValidDate(editRegistro.datahora))}
                  helperText={editRegistro.datahora !== undefined && (editRegistro.datahora === '' || !isValidDate(editRegistro.datahora)) ? 'Data/hora é obrigatória e deve ser válida' : ''}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    sx: { 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Título"
                  value={editRegistro.titulo || ''}
                  onChange={(e) => handleChangeEditRegistro('titulo', e.target.value)}
                  InputProps={{
                    sx: { 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Duração (segundos)"
                  type="number"
                  value={editRegistro.duracao || ''}
                  onChange={(e) => handleChangeEditRegistro('duracao', parseInt(e.target.value))}
                  InputProps={{
                    sx: { 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Custo"
                  type="number"
                  value={editRegistro.custo || ''}
                  onChange={(e) => handleChangeEditRegistro('custo', parseFloat(e.target.value))}
                  InputProps={{
                    sx: { 
                      borderRadius: 2,
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 0 0 2px rgba(0,0,0,0.05)'
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`
                      }
                    },
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={emissorasDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Emissora" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={emissorasDisponiveis.find(e => e.id === editRegistro.emissora_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('emissora_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={anunciantesDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Anunciante" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={anunciantesDisponiveis.find(a => a.id === editRegistro.anunciante_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('anunciante_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={marcasDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Marca" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={marcasDisponiveis.find(m => m.id === editRegistro.marca_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('marca_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={itensDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Item" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={itensDisponiveis.find(i => i.id === editRegistro.item_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('item_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={programasDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Programa" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={programasDisponiveis.find(p => p.id === editRegistro.programa_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('programa_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={categoriasDisponiveis}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Categoria" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={categoriasDisponiveis.find(c => c.id === editRegistro.categoria_id) || null}
                    onChange={(_, newValue) => {
                      handleChangeEditRegistro('categoria_id', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={[
                      { id: 'A', nome: 'A - Inicio/Encerramento Controle' },
                      { id: 'a', nome: 'a - Patrocício de Vinheta de Programa' },
                      { id: 'N', nome: 'N - Comercial' },
                      { id: 'M', nome: 'M - Merchandising' },
                      { id: 'P', nome: 'P - Programa' },
                      { id: 'p', nome: 'p - Patrocínio de Vinheta de Passagem' },
                      { id: 'I', nome: 'I - Interprograma' },
                      { id: 'V', nome: 'V - Vinheta' },
                      { id: 'v', nome: 'v - Patrocínio de Vinheta' },
                      { id: 'X', nome: 'X - Eventos' },
                      { id: 'x', nome: 'x - Patrocínio de Eventos' },
                      { id: 'Z', nome: 'Z - Comercial Político' },
                      { id: 'C', nome: 'C - Chamada' },
                      { id: 'c', nome: 'c - Patrocínio de Chamada' },
                      { id: 'B', nome: 'B - Break' },
                      { id: 'b', nome: 'b - Patrocínio de Boletim' },
                      { id: 'T', nome: 'T - Top' },
                      { id: 't', nome: 't - Patrocínio de Top' },
                      { id: 'S', nome: 'S - Subprograma' },
                      { id: 'Y', nome: 'Y - Encerramento de Subprograma' },
                      { id: 'Q', nome: 'Q - Quadro' },
                      { id: 'E', nome: 'E - Encerramento do Quadro' },
                      { id: 'e', nome: 'e - Patrocínio de Encerramento de Programa' },
                      { id: 'F', nome: 'F - Falha Técnica de Controle' }
                    ]}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Tipo" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={editRegistro.tipo ? { id: editRegistro.tipo, nome: editRegistro.tipo } as any : null}
                    onChange={(_, newValue: any) => {
                      handleChangeEditRegistro('tipo', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    options={[
                      { id: 1, nome: '1 - Baixa' },
                      { id: 2, nome: '2 - Média' },
                      { id: 3, nome: '3 - Alta' }
                    ]}
                    getOptionLabel={(option) => option.nome}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label="Precisão" 
                        variant="outlined"
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          sx: { borderRadius: 2 }
                        }}
                      />
                    )}
                    value={editRegistro.precisao ? { id: editRegistro.precisao, nome: `${editRegistro.precisao}` } as any : null}
                    onChange={(_, newValue: any) => {
                      handleChangeEditRegistro('precisao', newValue?.id || null);
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Considerações"
                  value={editRegistro.consideracoes || ''}
                  onChange={(e) => handleChangeEditRegistro('consideracoes', e.target.value)}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PI Number"
                  value={editRegistro.pi_number || ''}
                  onChange={(e) => handleChangeEditRegistro('pi_number', e.target.value)}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.background.paper, 0.97) }}>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            variant="outlined"
            sx={{ borderRadius: 2, mr: 1 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEditRegistro}
            disabled={!editRegistro.titulo || !editRegistro.datahora || !isValidDate(editRegistro.datahora)}
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
              px: 3,
              py: 1,
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              }
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
};

export default RegistrosPage; 